import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Package, ArrowsLeftRight } from "@phosphor-icons/react";

/**
 * ReceiveBatchDialog — admin records a physical batch arriving at the lab.
 * Surfaces directly on each material row in the Materials tab.
 *
 * Endpoint: POST /api/admin/inventory/batches
 *
 * iter97.33 — Now pack-aware. If the material has `purchase_uom` + `pack_size`
 * configured (e.g. "box" of 50 sheets), the dialog defaults to pack-mode
 * input ("5 boxes") and shows the live conversion preview ("= 250 sheets").
 * The backend handles the multiplication so current_stock stays in the
 * consumption UoM.
 */
export function ReceiveBatchDialog({ material, open, onClose, onSaved }) {
    const [form, setForm] = useState({});
    const [busy, setBusy] = useState(false);

    const packSize = Number(material?.pack_size || 1);
    const purchaseUom = (material?.purchase_uom || "").trim();
    const consumptionUom = material?.unit || material?.consumption_uom || "";
    const hasPack = packSize > 1 && !!purchaseUom;

    useEffect(() => {
        if (open) {
            const today = new Date().toISOString().slice(0, 10);
            setForm({
                lot_number: "",
                supplier: "",
                received_date: today,
                expiry_date: "",
                manufacture_date: "",
                qty: 0,
                /* iter97.33 — Default to pack-mode when the material declares
                   a pack. Admin can flip the toggle to enter pieces directly
                   (e.g. for half-boxes / opened cartons). */
                qty_in_packs: hasPack,
                unit_cost: material?.unit_cost_estimate || 0,
                notes: "",
            });
        }
    }, [open, material, hasPack]);

    const qtyNum = Number(form.qty) || 0;
    /* Live conversion: how many CONSUMPTION units the batch will add. */
    const consumptionQty = form.qty_in_packs && hasPack ? qtyNum * packSize : qtyNum;
    const inputUomLabel = form.qty_in_packs && hasPack ? purchaseUom : consumptionUom;

    const submit = async () => {
        if (!form.lot_number?.trim()) { toast.error("Lot number is required"); return; }
        if (!qtyNum || qtyNum <= 0) { toast.error("Quantity must be > 0"); return; }
        setBusy(true);
        try {
            await api.post("/admin/inventory/batches", {
                material_id: material.id,
                lot_number: form.lot_number.trim(),
                supplier: form.supplier?.trim() || "",
                received_date: form.received_date || null,
                expiry_date: form.expiry_date || null,
                manufacture_date: form.manufacture_date || null,
                qty: qtyNum,
                qty_in_packs: !!form.qty_in_packs,
                unit_cost: Number(form.unit_cost) || 0,
                notes: form.notes?.trim() || "",
            });
            const packLabel = (qtyNum === 1 || !hasPack) ? purchaseUom : (purchaseUom + (purchaseUom.endsWith("s") || /(x|ch|sh)$/.test(purchaseUom) ? "es" : "s"));
            const summary = form.qty_in_packs && hasPack
                ? `+${qtyNum} ${packLabel} (${consumptionQty} ${consumptionUom})`
                : `+${qtyNum} ${consumptionUom}`;
            toast.success(`Batch received — ${material.sku} ${summary}`);
            onSaved?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to record batch");
        } finally {
            setBusy(false);
        }
    };

    if (!material) return null;
    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
            <DialogContent className="max-w-lg" data-testid="receive-batch-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Package size={20} weight="fill" className="text-primary" />
                        Receive batch — {material.sku}
                    </DialogTitle>
                    <DialogDescription>
                        Record a physical arrival of <span className="font-semibold">{material.name}</span>. The lot number + dates let the doctor trace which exact sheets were used on their case.
                    </DialogDescription>
                </DialogHeader>

                <div className="grid grid-cols-2 gap-3">
                    <div className="col-span-2">
                        <Label>Lot number<span className="text-rose-600">*</span></Label>
                        <Input
                            value={form.lot_number || ""}
                            onChange={(e) => setForm({ ...form, lot_number: e.target.value })}
                            placeholder="e.g. L26-0231"
                            className="font-mono"
                            data-testid="batch-lot-input"
                        />
                    </div>
                    <div>
                        <Label>Supplier</Label>
                        <Input
                            value={form.supplier || ""}
                            onChange={(e) => setForm({ ...form, supplier: e.target.value })}
                            placeholder="Bay Materials"
                            data-testid="batch-supplier-input"
                        />
                    </div>

                    {/* iter97.33 — Quantity block with pack/piece toggle. */}
                    <div className="col-span-2 rounded-lg border-2 border-emerald-200 bg-emerald-50/50 p-3 space-y-2.5" data-testid="batch-qty-block">
                        <div className="flex items-center justify-between gap-3">
                            <Label className="m-0">
                                Quantity ({inputUomLabel})<span className="text-rose-600">*</span>
                            </Label>
                            {hasPack && (
                                <button
                                    type="button"
                                    onClick={() => setForm({ ...form, qty_in_packs: !form.qty_in_packs })}
                                    className="text-[10px] inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-card border border-emerald-300 font-bold uppercase tracking-wider text-emerald-900 hover:bg-emerald-100"
                                    data-testid="batch-qty-toggle"
                                >
                                    <ArrowsLeftRight size={11} />
                                    Switch to {form.qty_in_packs ? consumptionUom : purchaseUom}
                                </button>
                            )}
                        </div>
                        <Input
                            type="number"
                            min="0"
                            step={form.qty_in_packs ? "1" : "0.01"}
                            value={form.qty || ""}
                            onChange={(e) => setForm({ ...form, qty: e.target.value })}
                            placeholder={form.qty_in_packs ? "5" : "100"}
                            data-testid="batch-qty-input"
                        />
                        {hasPack && (
                            <div className="text-[11px] leading-snug text-emerald-900 font-medium" data-testid="batch-qty-preview">
                                {qtyNum > 0
                                    ? form.qty_in_packs
                                        ? <>= <span className="font-bold tabular-nums">{consumptionQty.toLocaleString("en-IN")}</span> {consumptionUom} into stock <span className="text-emerald-700/70">(1 {purchaseUom} = {packSize} {consumptionUom})</span></>
                                        : <>≈ <span className="font-bold tabular-nums">{(qtyNum / packSize).toLocaleString("en-IN", { maximumFractionDigits: 2 })}</span> {purchaseUom} <span className="text-emerald-700/70">(at {packSize} {consumptionUom}/{purchaseUom})</span></>
                                    : <span className="text-muted-foreground">Enter a quantity to see the conversion</span>}
                            </div>
                        )}
                    </div>

                    <div>
                        <Label>Received date</Label>
                        <Input
                            type="date"
                            value={form.received_date || ""}
                            onChange={(e) => setForm({ ...form, received_date: e.target.value })}
                            data-testid="batch-received-date"
                        />
                    </div>
                    <div>
                        <Label>Manufacture date</Label>
                        <Input
                            type="date"
                            value={form.manufacture_date || ""}
                            onChange={(e) => setForm({ ...form, manufacture_date: e.target.value })}
                            data-testid="batch-mfg-date"
                        />
                    </div>
                    <div>
                        <Label>Expiry date</Label>
                        <Input
                            type="date"
                            value={form.expiry_date || ""}
                            onChange={(e) => setForm({ ...form, expiry_date: e.target.value })}
                            data-testid="batch-expiry-date"
                        />
                    </div>
                    <div>
                        <Label>Unit cost (₹ per {hasPack && form.qty_in_packs ? purchaseUom : consumptionUom})</Label>
                        <Input
                            type="number"
                            value={form.unit_cost ?? ""}
                            onChange={(e) => setForm({ ...form, unit_cost: e.target.value })}
                            placeholder="80"
                            data-testid="batch-unit-cost"
                        />
                    </div>
                    <div className="col-span-2">
                        <Label>Notes</Label>
                        <Textarea
                            value={form.notes || ""}
                            onChange={(e) => setForm({ ...form, notes: e.target.value })}
                            rows={2}
                            placeholder="Shelf B-3, supplier QC pass, etc."
                            data-testid="batch-notes"
                        />
                    </div>
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
                    <Button onClick={submit} disabled={busy} data-testid="batch-save-btn">
                        {busy ? "Recording…" : "Record batch"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default ReceiveBatchDialog;
